import { EnvironmentService } from 'src/app/services/environment.service';
import { ReviewSectionStore } from './review-section.store';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  SectionIdentifier,
  SectionMessage,
  SectionReview,
  SectionStatus,
  ShowButtonsPredicateContext,
  SkExecutorAction,
} from './review-section.domain';
import { ReviewSectionService } from './review-section.service';
import { map } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { ReviewSectionSelectors } from './review-section.selectors';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { OrderProcesStore } from '../../stores/order-process.store';
import { combineLatest } from 'rxjs';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { parseIntOrNull } from '../../helpers/parsers.helper';

const defaultActiveExecutorTypes: SkExecutorAction[] = [
  SkExecutorAction.accept_application,
  SkExecutorAction.accept_offer,
  SkExecutorAction.create_offer,
  SkExecutorAction.request_update_application,
  SkExecutorAction.update_application,
  SkExecutorAction.approve_offer,
];

const chimneySweeperActiveExecutorTypes: SkExecutorAction[] = [
  SkExecutorAction.cs_accept_application,
  SkExecutorAction.cs_reject_application,
];

export const hideButtonsForChimneySweeper = (context: ShowButtonsPredicateContext) => {
  return context.orderProcess.chimney_sweeper_id && context.orderProcess.chimney_sweeper_id !== '0'
    ? context.userRole !== 'chimney_sweeper'
    : true;
};

export const hideButtonsForEmployeeWhenHasChimneySweeper = (context: ShowButtonsPredicateContext) => {
  return context.orderProcess.chimney_sweeper_id && context.orderProcess.chimney_sweeper_id !== '0'
    ? context.userRole !== 'employee'
    : true;
};

@Component({
  selector: 'app-review-section',
  templateUrl: './review-section.component.html',
  styleUrls: ['./review-section.component.scss'],
})
export class ReviewSectionComponent extends DestroyableComponent implements DestroyableComponent, OnChanges {
  review: SectionReview;
  areButtonsDisabled = false;
  isInternalUser: boolean;

  showUnlockTooltip = true;
  showLockTooltip = true;
  showComments = true;
  sectionMessages: SectionMessage[];

  showRequestChangeBtn = this.environmentService.showRequestChangeBtn;
  _applicationId: number;
  canCreateMessages: boolean;
  applicationHasChimneySweeper: boolean;
  public isDataEditable: boolean = true;

  @Output() onChanges = new EventEmitter<ReviewSectionComponent>();
  @Output() delete = new EventEmitter<ReviewSectionComponent>();
  @Output() onSectionReviewCreated = new EventEmitter<SectionReview>(); // related to https://github.com/bittner-krull/gop-web/issues/2062

  private currentUserId = this.localStorageService.getItem('technician_id');

  constructor(
    private reviewSectionService: ReviewSectionService,
    public reviewSectionStore: ReviewSectionStore,
    private orderProcessStore: OrderProcesStore,
    private userService: UserService,
    private reviewSectionSelectors: ReviewSectionSelectors,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.onChanges.emit(this);
  }

  @Input() title: string;
  @Input() tooltip: string;
  @Input() showTooltip: boolean = true;
  @Input() tooltipStyleClass: string = 'netz-info-tooltip';
  @Input() tooltipPosition: 'left' | 'bottom' = 'left';
  @Input() sectionId: SectionIdentifier;
  @Input() set applicationId(value: number | string) {
    if (!value) return;
    this._applicationId = parseInt(value.toString());
  }
  @Input() isDetailsView: boolean;
  @Input() showAcceptRejectButtons: boolean = true;
  @Input() showMessages = true;

  @Input() areButtonsVisible: boolean = true;
  /// If the user has any of the roles in the excludeRoles array, the buttons will be disabled
  @Input() showButtonsPredicate: (context: ShowButtonsPredicateContext) => boolean;
  @Input() checkIfIsEditable: any = null;
  @Input() forceAllowAddMessages: boolean | null = null;
  @Input() deletable: boolean = false;
  @Input() hasBorders: boolean = false;
  @Input() entityId: number | null = null;

  sectionStatus = {
    approved: SectionStatus.approved,
    rejected: SectionStatus.rejected,
  };

  ngOnInit(): void {
    if (this.entityId != null && typeof this.entityId !== 'number') {
      throw 'Invalid value for entityId';
    }

    this.areButtonsDisabled = !this.userService.isUserModeratorRole();

    if (this.showMessages) {
      this.subscribeToSectionMessages();
    }

    this.subscribeToSectionWaitingForUpdateCheck();

    this.checkButtonsActivity(() => {
      this.addSectionReviewToStoreIfNeeded();
    });
  }

  onDelete() {
    this.delete.emit(this);
  }

  private checkButtonsActivity(onSuccess?: any) {
    this.preventLeak(this.orderProcessStore.getOrderProcessValue()).subscribe(orderProcess => {
      const activeExecutorTypes = this.userService.isChimneySweeper() ? chimneySweeperActiveExecutorTypes : defaultActiveExecutorTypes;
      const isLocked = !activeExecutorTypes.map(x => x.toString()).includes(orderProcess?.sk_executor_action);
      this.canCreateMessages = (this.forceAllowAddMessages !== null && this.forceAllowAddMessages) || !isLocked;

      if (this.userService.isChimneySweeper()) {
        this.areButtonsDisabled = isLocked;
      } else {
        this.areButtonsDisabled = isLocked || !this.userService.isUserModeratorRole();
      }

      if (!orderProcess) {
        if (onSuccess) onSuccess();
        this.onChanges.emit(this);
        return;
      }

      if (this.showButtonsPredicate) {
        this.areButtonsVisible = this.showButtonsPredicate({ component: this, orderProcess, userRole: this.userService.getUserRole() });
      }

      if (onSuccess) {
        onSuccess();
      }

      this.onChanges.emit(this);
    });
  }

  private addSectionReviewToStoreIfNeeded() {
    this.preventLeak(this.reviewSectionSelectors.getSectionReview(this._applicationId, this.sectionId, this.entityId)).subscribe(
      async review => {
        if (!review && this.showAcceptRejectButtons && this.areButtonsVisible) {
          /* adding a review section for state tracking purposes if the review for
         this section is not yet available*/
          this.reviewSectionStore.updateReview({
            application_id: this._applicationId,
            id: 0,
            entity_id: this.entityId,
            reviewed_on: new Date(),
            reviewer_id: 0,
            section: this.sectionId,
            status: SectionStatus.none,
          });
        }

        this.review = review;

        this.onChanges.emit(this);
      }
    );
  }

  private subscribeToSectionMessages() {
    this.preventLeak(
      this.reviewSectionStore.sectionMessages.pipe(
        map(messages =>
          messages.filter((message: SectionMessage) => {
            return message.section === this.sectionId && message.entity_id === this.entityId;
          })
        ),
        map(messages =>
          messages.map(m => {
            m.content = m.content.replace(/\n/g, '<br/>'); // Fix the new lines
            return m;
          })
        )
      )
    ).subscribe(messages => (this.sectionMessages = messages));
  }

  private subscribeToSectionWaitingForUpdateCheck() {
    this.preventLeak(
      combineLatest([
        this.reviewSectionSelectors.isSectionApproved(this._applicationId, this.sectionId, this.entityId),
        this.orderProcessStore.getOrderProcessValue(),
      ])
    ).subscribe(([isSectionApproved, orderProcess]) => {
      if (this.checkIfIsEditable) {
        this.isDataEditable = this.checkIfIsEditable({
          canEditSection: !isSectionApproved,
          orderProcess,
          isDetailsView: this.isDetailsView,
        });
      } else {
        if (this.isDetailsView) {
          this.isDataEditable =
            this.showRequestChangeBtn &&
            !isSectionApproved &&
            orderProcess &&
            orderProcess?.sk_executor_action === 'update_application' &&
            orderProcess.application_user_id === this.currentUserId;
        } else {
          this.isDataEditable = true;
        }
      }

      this.onChanges.emit(this);
    });
  }

  createSectionReview(status: SectionStatus) {
    if (this.areButtonsDisabled) return;

    this.preventLeak(
      this.reviewSectionService.createSectionReview({
        application_id: this._applicationId,
        section: this.sectionId,
        status: status,
        entity_id: parseIntOrNull(this.entityId),
      })
    ).subscribe(review => {
      this.reviewSectionStore.updateReview(review);
      this.onSectionReviewCreated.emit(review);
    });
  }
}
